import {announce} from '@github-ui/aria-live'
import {updateUrlHash} from '@github-ui/history'
import type {SourceRange, SourceSymbol} from '@github-ui/taillights-worker/document'
import {useDebounce} from '@github-ui/use-debounce'
import {createContext, type ReactNode, startTransition, use, useCallback, useMemo, useRef, useState} from 'react'

import {ExperimentalSourceOptionsContext} from './experimental-source-options'
import {useCodeBlobSource} from './source-context'
import {anchorFromSelection, displayedRange, lineRange} from './source-navigation'
import {windowedSourceChunkLines} from './source-window'
import {useGutterSelection} from './use-gutter-selection'
import {useSourceNavigation} from './use-source-navigation'
import {useSourceSearch} from './use-source-search'

const emptyRanges: readonly SourceRange[] = []
const maxRetainedSourceLines = 300
export const SourceModelContext = createContext<ReturnType<typeof useSourceModel> | null>(null)

export type SourceModelOptions = {
  foldingAvailable?: boolean
  onStickyLineRemoval?: (originalLineNumber: number) => void
  onSectionCollapse?: (originalLineNumber: number) => void
  showLineActions?: boolean
  materializeAllLines?: boolean
  routerHash?: string
  hashNavigationOffset?: number
}

function useSourceModel({
  foldingAvailable = true,
  onStickyLineRemoval,
  onSectionCollapse,
  showLineActions = false,
  materializeAllLines = false,
  routerHash,
  hashNavigationOffset = 0,
}: SourceModelOptions) {
  const source = useCodeBlobSource()
  const options = use(ExperimentalSourceOptionsContext)
  const shouldVirtualize = options.virtualized ?? source.lines.length > maxRetainedSourceLines
  const virtualized = !materializeAllLines && shouldVirtualize
  const reverseSticky = virtualized && (options.experimentalReverseSticky ?? true)
  const linesPerChunk = options.linesPerChunk ?? (shouldVirtualize ? windowedSourceChunkLines : 60)
  const linesToBuffer = options.linesToBuffer ?? (reverseSticky ? 0 : undefined)
  const announceFindResults = useDebounce((text: string) => announce(text), 300, {onChangeBehavior: 'cancel'})
  const announceStatus = useCallback(
    (text: string) => {
      announceFindResults.cancel()
      announce(text)
    },
    [announceFindResults],
  )
  const [foldingPreference, setFoldingPreference] = useState(true)
  const scrollSurfaceRef = useRef<HTMLDivElement>(null)
  const stickyOffsetRef = useRef<HTMLDivElement>(null)
  const stickyWindowRef = useRef<HTMLDivElement>(null)
  const sourcePlaneRef = useRef<HTMLDivElement>(null)
  const reverseStickyRefs = useMemo(
    () => ({offsetRef: stickyOffsetRef, windowRef: stickyWindowRef, sourceRef: sourcePlaneRef}),
    [],
  )
  const navigation = useSourceNavigation(
    source,
    announceStatus,
    foldingAvailable && foldingPreference,
    onStickyLineRemoval,
    linesPerChunk,
    reverseSticky ? scrollSurfaceRef : undefined,
    onSectionCollapse,
    routerHash,
    hashNavigationOffset,
  )
  const {lines, symbolsRef, anchorRange, setAnchorRange, navigate, visible, view, lineHeight} = navigation
  const fallbackText = navigation.textProjection.text
  const [message, setMessage] = useState('')
  const [renderFailure, setRenderFailure] = useState<{
    controller: typeof navigation.controller
    message: string
  } | null>(null)
  const renderError = renderFailure?.controller === navigation.controller ? renderFailure.message : ''
  const onRenderError = useCallback(
    (failure: Error) => {
      if (failure.name !== 'AbortError') reportError(failure)
      setRenderFailure({controller: navigation.controller, message: failure.message})
      announceStatus('Syntax rendering failed. Native text remains available.')
    },
    [announceStatus, navigation.controller],
  )
  const search = useSourceSearch(
    'text',
    (result, focus) => {
      if (result.current) navigate(result.current, focus)
      const text = result.current
        ? `Find in file: ${result.count} results. Result ${result.index + 1} of ${result.count}, line ${result.current.start.line + 1}.`
        : 'No results in this source.'
      if (focus) {
        announceStatus(
          result.current
            ? `Result ${result.index + 1} of ${result.count}, line ${result.current.start.line + 1}.`
            : text,
        )
      } else announceFindResults(text)
    },
    onRenderError,
  )
  const symbolSearch = useSourceSearch(
    'symbol',
    result => announceStatus(`${result.count} text references to ${result.query}.`),
    onRenderError,
  )
  const references = symbolSearch.result?.highlights ?? emptyRanges
  const fullLineRange =
    anchorRange?.start.column === 0 && anchorRange.end.column === lines[anchorRange.end.line]?.length
      ? anchorRange
      : null
  const lineHighlight = useMemo(() => {
    if (!fullLineRange || !view) return null
    const displayed = displayedRange(visible, fullLineRange)
    return displayed
      ? {top: displayed.start * lineHeight, height: (displayed.end - displayed.start) * lineHeight}
      : null
  }, [fullLineRange, view, lineHeight, visible])
  const highlightRanges = useMemo(() => {
    const highlights = search.query ? (search.result?.highlights ?? emptyRanges) : references
    return anchorRange && !fullLineRange ? [...highlights, anchorRange] : highlights
  }, [search.query, search.result, references, anchorRange, fullLineRange])
  const focusedResult = search.query ? (search.result?.current ?? null) : null
  const activeRange = useCallback(
    () => symbolsRef.current?.getSelection() ?? anchorRange ?? lineRange(lines, 0),
    [symbolsRef, anchorRange, lines],
  )
  const highlightLines = useCallback(
    (range: SourceRange) => {
      setAnchorRange(range)
      updateUrlHash(anchorFromSelection(range, lines))
      announceStatus(
        range.start.line === range.end.line
          ? `Line ${range.start.line + 1} highlighted.`
          : `Lines ${range.start.line + 1} to ${range.end.line + 1} highlighted.`,
      )
    },
    [setAnchorRange, announceStatus, lines],
  )
  function moveResult(delta: number) {
    if (search.query) search.move(delta)
    else announceStatus('Enter text to find in this source.')
  }
  function changeQuery(value: string) {
    navigation.cancelPending()
    search.changeQuery(value)
    if (!value) announceStatus('Find in file cleared.')
  }
  const reportMessage = useCallback(
    (text: string) => {
      setMessage(text)
      announceStatus(text)
    },
    [announceStatus],
  )
  const gutterSelection = useGutterSelection({navigation, reportMessage, highlightLines, showLineActions})
  const changeFindQuery = search.changeQuery
  const changeSymbolQuery = symbolSearch.changeQuery
  const cancelNavigation = navigation.cancelPending
  const isCurrentSearch = search.isCurrent
  const searchRevision = search.revision
  const onSymbolSelected = useCallback(
    (symbol: SourceSymbol) => {
      if (isCurrentSearch(searchRevision)) {
        startTransition(() => {
          cancelNavigation()
          changeFindQuery('')
          changeSymbolQuery(symbol.text)
        })
      }
    },
    [cancelNavigation, changeFindQuery, changeSymbolQuery, isCurrentSearch, searchRevision],
  )
  return {
    navigation,
    scrollPaddingTop: hashNavigationOffset,
    materializeAllLines,
    virtualized,
    reverseSticky,
    linesToBuffer,
    linesPerChunk,
    scrollSurfaceRef,
    stickyOffsetRef,
    stickyWindowRef,
    sourcePlaneRef,
    reverseStickyRefs,
    fallbackText,
    foldingPreference,
    setFoldingPreference,
    announceStatus,
    message,
    renderError,
    onRenderError,
    search,
    symbolSearch,
    lineHighlight,
    highlightRanges,
    focusedResult,
    activeRange,
    highlightLines,
    moveResult,
    changeQuery,
    reportMessage,
    gutterSelection,
    onSymbolSelected,
  }
}

/** Shares source interaction state with host controls; never creates a controller or worker. */
export function SourceModelProvider({children, ...options}: SourceModelOptions & {children: ReactNode}) {
  const model = useSourceModel(options)
  return <SourceModelContext value={model}>{children}</SourceModelContext>
}

export function useSourceModelContext() {
  const model = use(SourceModelContext)
  if (!model) throw new Error('Source controls require a SourceModelProvider.')
  return model
}
